import { useRef, type ChangeEvent, type ReactNode } from 'react';
import { Button, type ButtonProps } from './button';

export type FileButtonProps = Omit<ButtonProps, 'onClick' | 'asChild' | 'type'> & {
  /** File types as for `<input accept>`, e.g. `image/png,image/svg+xml`. */
  accept: string;
  /**
   * Opens the camera of a phone or tablet right away instead of the file dialog: `environment`
   * the back camera, `user` the front camera. Desktop browsers ignore it.
   */
  capture?: 'user' | 'environment';
} & (
    | { multiple?: false; onFile: (file: File) => void; onFiles?: never }
    | { multiple: true; onFiles: (files: File[]) => void; onFile?: never }
  );

/**
 * Button that opens the file dialog of the device and hands over the chosen file, with
 * `multiple` all chosen files.
 */
export function FileButton({
  accept,
  capture,
  multiple,
  onFile,
  onFiles,
  disabled,
  ...button
}: FileButtonProps) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <Button {...button} disabled={disabled} onClick={() => input.current?.click()} />
      <input
        ref={input}
        type="file"
        accept={accept}
        capture={capture}
        multiple={multiple}
        hidden
        tabIndex={-1}
        disabled={disabled}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          const files = Array.from(event.target.files ?? []);
          // Reset, so choosing the same file again still triggers a change.
          event.target.value = '';
          if (!files.length) return;
          if (multiple) onFiles?.(files);
          else onFile?.(files[0]!);
        }}
      />
    </>
  );
}

/**
 * Opens the file dialog of the device from somewhere other than a button, e.g. an entry of a
 * dropdown menu, with the accepted file types per call. Render `input` once next to the trigger.
 */
export function useFilePicker(onFile: (file: File) => void): {
  open: (accept: string) => void;
  input: ReactNode;
} {
  const ref = useRef<HTMLInputElement>(null);
  const open = (accept: string) => {
    if (!ref.current) return;
    ref.current.accept = accept;
    ref.current.click();
  };
  const input = (
    <input
      ref={ref}
      type="file"
      hidden
      tabIndex={-1}
      data-file-picker=""
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        // Reset, so choosing the same file again still triggers a change.
        event.target.value = '';
        if (file) onFile(file);
      }}
    />
  );
  return { open, input };
}
